'use client'

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { getMonth, isAfter, isBefore, type Locale, startOfDay } from 'date-fns'
import { enUS, es, fr, it } from 'date-fns/locale'

interface DatePickerAirbnbProps {
  value: Date | null
  onChange: (date: Date | null) => void
  onBlur?: () => void
  label: string
  placeholder?: string
  error?: boolean
  helperText?: string
  disabled?: boolean
  disabledDates?: Date[]
  minDate?: Date
  maxDate?: Date
  /** Block past dates. Defaults to true. Set false for birth dates etc. */
  disablePast?: boolean
  /** 0-indexed months to disable entirely (0 = Jan, 6 = Jul, 7 = Aug …) */
  disabledMonths?: number[]
}

export const DatePickerAirbnb: React.FC<DatePickerAirbnbProps> = ({
  value,
  onChange,
  onBlur,
  label,
  placeholder,
  error = false,
  helperText,
  disabled = false,
  disabledDates = [],
  minDate,
  maxDate,
  disablePast = true,
  disabledMonths = []
}) => {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)

  const localeMap: Record<string, Locale> = {
    it,
    en: enUS,
    fr,
    es
  }

  const locale = localeMap[i18n.language] || enUS
  const resolvedPlaceholder = placeholder ?? 'GG/MM/AAAA'

  const today = useMemo(() => startOfDay(new Date()), [])

  const hasCustomDisable = disabledDates.length > 0 || disabledMonths.length > 0

  const shouldDisableDate = (date: Date): boolean => {
    const d = startOfDay(date)

    if (disablePast && isBefore(d, today)) return true

    if (disabledMonths.length > 0 && disabledMonths.includes(getMonth(d))) return true

    if (disabledDates.some((dd) => startOfDay(dd).getTime() === d.getTime())) return true

    if (minDate && isBefore(d, startOfDay(minDate))) return true
    if (maxDate && isAfter(d, startOfDay(maxDate))) return true

    return false
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <DatePicker
        label={label}
        value={value}
        onChange={onChange}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disabled={disabled}
        shouldDisableDate={hasCustomDisable ? shouldDisableDate : undefined}
        disablePast={disablePast}
        minDate={minDate}
        maxDate={maxDate}
        slotProps={{
          textField: {
            variant: 'standard',
            fullWidth: true,
            error,
            helperText,
            onBlur,
            onClick: () => {
              if (!disabled) setOpen(true)
            },
            slotProps: {
              inputLabel: { shrink: true }
            },
            sx: {
              '& .MuiInputLabel-root': {
                transform: 'translate(0, -1.5px) scale(0.75)',
                transformOrigin: 'top left'
              },
              '& .MuiInputBase-input, & .MuiPickersSectionList-root': {
                fontSize: '1.9rem'
              },
              '& .MuiPickersSectionList-sectionContent': {
                fontSize: '1.9rem'
              }
            }
          },
          layout: {
            sx: {
              '.MuiPickersDay-root.Mui-disabled:not(.Mui-selected)': {
                color: 'error.main',
                textDecoration: 'line-through',
                opacity: 1
              }
            }
          } as Record<string, unknown>
        }}
      />
    </LocalizationProvider>
  )
}
